import { useEffect, useRef, useState } from "react";
import { useApi } from "../../hooks/useApi";
import { useNavigate, useParams } from "react-router-dom";
import { useObjectState } from "../../hooks/useObjectState";
import { Popup } from "../layout/Popup";
import { FaArrowLeft, FaArrowRight, FaBan, FaSave, FaTrash } from "react-icons/fa";
import { Button } from "../layout/Button";
import { useTranslations } from "../../layers/Localization";
import { TabbedForm, Tabs } from "./Tabs";

export function DetailPage({ children, model, title, Fields, defaults = {}, actions = {}, tabs = [], onItemLoad, relation, tables, nextKey, nextQuery }) {
    const query = new URLSearchParams(window.location.search)
    let queryDefault = {}
    try {
        queryDefault = JSON.parse(query.get('default'))
    } catch (err) { }


    const [originalItem, setOriginalItem] = useState({ ...defaults, ...queryDefault })
    const [item, setItem, set] = useObjectState({ ...defaults, ...queryDefault })
    const [createdBy, setCreatedBy] = useState({})
    const [updatedBy, setUpdatedBy] = useState({})
    const [popup, setPopup] = useState(false)
    const [tab, setTab] = useState(tabs[0])
    let [editAfterSubmit, setEditAfterSubmit] = useState(false)
    const [previous, setPrevious] = useState()
    const [next, setNext] = useState()

    const { id, relatedId } = useParams()
    const navigate = useNavigate()
    const api = useApi()
    const t = useTranslations()
    const formContainer = useRef()

    const isNew = id === 'new'
    const back = () => query.get('next') === 'close' ? window.close() : navigate(query.get('next') || './..')
    const isReady = id === item.id || (id === 'new' && !item.id)

    useEffect(() => {
        if (!isReady) {
            if (id === 'new') {
                setItem({ ...defaults, ...queryDefault })
                onItemLoad?.(null)
            } else {
                api.get(`/${model}/${id}`).then(originalItem => {
                    setOriginalItem({ ...originalItem })
                    setItem({ ...originalItem })
                    onItemLoad?.({ ...originalItem })
                })
            }
        }
    }, [id])

    useEffect(() => {
        if (originalItem?.createdBy) {
            api.get(`/users/${originalItem.createdBy}`).then(user => {
                setCreatedBy(user)
                if (originalItem.createdBy === originalItem.updatedBy) setUpdatedBy(user)
            })
        }
        if (originalItem?.updatedBy && originalItem?.createdBy !== originalItem?.updatedBy) {
            api.get(`/users/${originalItem.updatedBy}`).then(setUpdatedBy)
        }
    }, [originalItem?.createdBy, originalItem?.updatedBy])

    useEffect(() => {
        if (nextKey && typeof originalItem?.[nextKey] === 'number') {
            api.get(`/${model}?limit=1&${encodeURIComponent(nextKey)}=${parseInt(originalItem[nextKey]) - 1}${nextQuery ? '&' + nextQuery : ''}`).then(items => setPrevious(items[0]))
            api.get(`/${model}?limit=1&${encodeURIComponent(nextKey)}=${parseInt(originalItem[nextKey]) + 1}${nextQuery ? '&' + nextQuery : ''}`).then(items => setNext(items[0]))
        }
    }, [originalItem?.[nextKey], nextQuery])

    useEffect(() => {
        if (isNew && relation) set(relation)(relatedId)
    }, [relation, isNew])

    const save = () => {
        editAfterSubmit = false
        setEditAfterSubmit(false)
        formContainer.current.querySelector('form').requestSubmit()
    }
    const saveAndEdit = () => {
        editAfterSubmit = true
        setEditAfterSubmit(true)
        formContainer.current.querySelector('form').requestSubmit()
    }
    const submit = async () => {
        if (isNew) {
            const { id } = await api.post(`/${model}`, item)
            if (editAfterSubmit) navigate(`./../${id}${window.location.search}`)
            else back()
        } else {
            const data = await api.patch(`/${model}/${id}`, item)
            if (editAfterSubmit) {
                setOriginalItem({ ...data })
                setItem({ ...data })
                onItemLoad?.({ ...data })
            } else back()
        }
    }
    const deleteItem = async () => await api.delete(`/${model}/${id}`).then(() => back())

    const fullActions = {
        [t('Delete')]: item => setPopup(true),
        ...actions
    }

    const noChanges = JSON.stringify(item) === JSON.stringify(originalItem)

    if (!isReady) return

    return <div className="w-full">
        <h1 className="font-bold text-2xl">{title}</h1>
        <div className="mt-3 flex flex-col-reverse md:flex-row gap-8">
            <div ref={formContainer} className="md:basis-3/4 flex flex-col gap-8">
                <TabbedForm onSubmit={submit} tabs={tabs} tab={tab} setTab={setTab}>
                    <div className="flex flex-col gap-5">
                        {isReady && <Fields item={item} set={set} originalItem={originalItem} tab={tab} />}
                    </div>
                    {isNew && <div className="flex items-center gap-5 mt-7">
                        <Button gray icon={<FaBan />} onClick={() => back()}>{t('Cancel')}</Button>
                        <Button icon={<FaSave />} onClick={save}>{t('Save')}</Button>
                        <Button icon={<FaSave />} onClick={saveAndEdit}>{t('Save & continue')}</Button>
                    </div>}
                </TabbedForm>
                {!isNew && tables && <Tabs tabs={tables} />}
                {children}
            </div>
            {!isNew && <div className="md:basis-1/4">
                <div className="bg-white border border-gray-300 rounded-xl overflow-hidden mb-8">
                    <div className="grid grid-cols-2 p-5 md:flex flex-col xl:grid md:gap-2 xl:gap-0">
                        <div>{t('Created at')}</div>
                        <div>{item.createdAt?.toLocaleString()}</div>
                        <div>{t('Created by')}</div>
                        <div>{createdBy?.email || '-'}</div>
                        <div>{t('Last update at')}</div>
                        <div>{item.updatedAt?.toLocaleString()}</div>
                        <div>{t('Last update by')}</div>
                        <div>{updatedBy?.email || '-'}</div>
                    </div>
                    {Object.keys(fullActions).map(key => fullActions[key]?.href
                        ? <a key={key} href={fullActions[key]?.href(originalItem)} className="block w-full text-center p-2 border-t border-gray-300 cursor-pointer hover:bg-gray-200">{key}</a>
                        : <button key={key} onClick={() => fullActions[key](originalItem, () => window.location.reload())} className="block w-full text-center p-2 border-t border-gray-300 cursor-pointer hover:bg-gray-200">{key}</button>
                    )}
                </div>
                <div className="bg-white border border-gray-300 rounded-xl p-5 flex flex-col gap-5">
                    <div className="flex items-center gap-5">
                        <Button className="grow justify-center" gray icon={<FaBan />} onClick={() => back()}>{t('Cancel')}</Button>
                        <Button className="grow justify-center" icon={<FaSave />} onClick={save} disabled={noChanges}>{t('Save')}</Button>
                    </div>
                    <Button className="justify-center" icon={<FaSave />} onClick={saveAndEdit} disabled={noChanges}>{t('Save & continue')}</Button>
                </div>
                {nextKey && <div className="bg-white border border-gray-300 rounded-xl p-5 mt-7">
                    <div className="flex items-center gap-5">
                        <Button className="grow justify-center" green={previous} gray={!previous} disabled={!previous} onClick={previous ? () => navigate(`/${model}/${previous.id}${window.location.search}`) : undefined} icon={<FaArrowLeft />}>{t('Previous')}</Button>
                        <Button className="grow justify-center" green={next} gray={!next} disabled={!next} onClick={next ? () => navigate(`/${model}/${next.id}${window.location.search}`) : undefined} icon={<FaArrowRight />} iconEnd>{t('Next')}</Button>
                    </div>
                </div>}
                <Popup value={popup} onChange={setPopup}>
                    <h2 className="text-xl mb-5">{t('Are you sure you want to delete this item ?')}</h2>
                    <div className="flex gap-3">
                        <Button gray icon={<FaBan />} onClick={() => setPopup(false)}>{t('Cancel')}</Button>
                        <Button red icon={<FaTrash />} onClick={deleteItem}>{t('Delete')}</Button>
                    </div>
                </Popup>
            </div>}
        </div>
    </div >
}

export function Row({ children, start, stretch, center, className }) {
    return <div className={`flex flex-col md:flex-row gap-5 ${start ? 'md:items-start' : stretch ? 'md:items-stretch' : center ? 'md:items-center' : 'md:items-start'} ${className || ''}`}>{children}</div>
}

export function Column({ children }) {
    return <div className={`grow flex flex-col gap-5 `}>{children}</div>
}